import { useState, useCallback } from "react";
import { listTickets, markTicketRead } from "../../../services/tickets.api";
import { getAccessSnapshot } from "../../../context/AccessControl/accessSnapshot";

/**
 * ¿Este ticket entra en el alcance del usuario?
 *
 * WHY mira TAMBIÉN los departamentos del equipo: el backend ya devuelve los
 * tickets correctos, pero este filtro los volvía a descartar usando solo
 * `user.queues` —la ficha—. Quien atendía un departamento **por pertenecer a un
 * equipo** y no lo tenía repetido en su ficha se quedaba con el contador a cero
 * y sin aviso, aunque el ticket sí le apareciera en la bandeja.
 *
 * Caso real (QA 2026-08-20): Paquita, Paulina, Omar y Jimena tenían la ficha
 * vacía y el departamento solo por equipo → badge 0. royalias, con el mismo
 * departamento repetido en su ficha, veía los 3. Mismo alcance, distinto aviso.
 *
 * El tramo de `qid == null` NO se toca: «solo quien tiene `allTicket` ve lo que
 * llega sin departamento» es otra regla, y sigue vigente (doc 0391 §11.4).
 */
function ticketIsInUserQueueScope(ticket, user, teamQueueIds = []) {
  if (!ticket || !user) return false;
  if (user.profile === "admin") return true;
  const queueIds = [
    ...(user.queues || []).map((q) => Number(q.id)),
    ...teamQueueIds.map(Number)
  ];
  const qid = ticket.queueId;
  if (qid == null || qid === "") return user.allTicket === "enabled";
  return queueIds.includes(Number(qid));
}

function ticketToNotification(ticket) {
  return {
    id: `ticket-${ticket.id}`,
    ticketId: ticket.id,
    contactId: ticket.contactId,
    message: ticket.lastMessage || "Nuevo mensaje",
    ticketStatus: ticket.status,
    queueName: ticket.queue?.name || "Sin departamento",
    tags: ticket.tags || [],
    contactName: ticket.contact?.name || "Contacto",
    contactNumber: ticket.contact?.number || "",
    contactAvatar: ticket.contact?.profilePicUrl || ticket.contact?.avatar || null,
    timestamp: ticket.updatedAt,
    unreadMessages: ticket.unreadMessages || 0,
    metadata: {
      ticketUuid: ticket.uuid,
      contactName: ticket.contact?.name || "Contacto",
      contactAvatar: ticket.contact?.profilePicUrl || ticket.contact?.avatar || null,
    },
    createdAt: ticket.updatedAt,
    isRead: ticket.unreadMessages === 0,
  };
}

const useNotificationsData = ({ user } = {}) => {
  const [notifications, setNotifications] = useState([]);

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await listTickets({ pageNumber: 1, showAll: true, withUnreadMessages: true });

      let ticketsWithUnread = (data.tickets || []).filter(
        (t) => t.unreadMessages > 0
      );

      if (user && user.profile !== "admin") {
        const { teamQueueIds } = getAccessSnapshot();
        ticketsWithUnread = ticketsWithUnread.filter((t) =>
          ticketIsInUserQueueScope(t, user, teamQueueIds)
        );
      }

      setNotifications(ticketsWithUnread.map(ticketToNotification));
    } catch (err) {
      console.error("[useNotificationsData] fetchNotifications error:", err);
    }
  }, [user]);

  const markAsRead = useCallback(async (ticketId) => {
    try {
      await markTicketRead(ticketId);
      setNotifications((prev) =>
        prev.map((n) =>
          n.ticketId === ticketId ? { ...n, isRead: true } : n
        )
      );
    } catch (err) {
      console.error("[useNotificationsData] markAsRead error:", err);
    }
  }, []);

  const applySocketMessage = useCallback((data) => {
    if (!data?.ticket || !data?.message) return;
    const { ticket, message, contact } = data;

    setNotifications((prev) => {
      const existing = prev.find((n) => n.ticketId === ticket.id);
      if (existing) {
        return prev.map((n) =>
          n.ticketId === ticket.id
            ? {
                ...n,
                message: message.body || n.message,
                ticketStatus: ticket.status || n.ticketStatus,
                timestamp: message.createdAt || n.timestamp,
                isRead: false,
                unreadMessages: typeof ticket.unreadMessages === "number" ? ticket.unreadMessages : (n.unreadMessages || 1),
              }
            : n
        );
      }
      return [
        {
          id: `ticket-${ticket.id}`,
          ticketId: ticket.id,
          contactId: contact?.id || ticket.contactId,
          message: message.body || "Nuevo mensaje",
          ticketStatus: ticket.status,
          queueName: ticket.queue?.name || "Sin departamento",
          tags: ticket.tags || [],
          contactName: contact?.name || "Contacto",
          contactNumber: contact?.number || "",
          contactAvatar: contact?.profilePicUrl || contact?.avatar || null,
          timestamp: message.createdAt || new Date().toISOString(),
          unreadMessages: ticket.unreadMessages || 1,
          metadata: {
            ticketUuid: ticket.uuid,
            contactName: contact?.name || "Contacto",
            contactAvatar: contact?.profilePicUrl || contact?.avatar || null,
          },
          createdAt: message.createdAt || new Date().toISOString(),
          isRead: false,
        },
        ...prev,
      ];
    });
  }, []);

  const applyTicketEvent = useCallback((data) => {
    if (!data?.ticket) return;
    const { ticket, action } = data;

    if (action === "delete") {
      setNotifications((prev) =>
        prev.filter((n) => n.ticketId !== ticket.id)
      );
      return;
    }

    // New ticket arrived at notification room — add it if it has unread messages.
    if (action === "create") {
      if (typeof ticket.unreadMessages === "number" && ticket.unreadMessages > 0) {
        setNotifications((prev) => {
          if (prev.find((n) => n.ticketId === ticket.id)) return prev;
          return [ticketToNotification(ticket), ...prev];
        });
      }
      return;
    }

    if (action === "update" && typeof ticket.unreadMessages === "number" && ticket.unreadMessages === 0) {
      setNotifications((prev) =>
        prev.map((n) =>
          n.ticketId === ticket.id ? { ...n, isRead: true, unreadMessages: 0 } : n
        )
      );
      return;
    }

    setNotifications((prev) =>
      prev.map((n) =>
        n.ticketId === ticket.id
          ? {
              ...n,
              ticketStatus: ticket.status,
              isRead: n.isRead,
              ...(typeof ticket.unreadMessages === "number" && ticket.unreadMessages > 0
                ? { unreadMessages: ticket.unreadMessages }
                : {}),
            }
          : n
      )
    );
  }, []);

  const applyContactDelete = useCallback((contactId) => {
    const id = Number(contactId);
    if (!id) return;
    setNotifications((prev) => prev.filter((n) => Number(n.contactId) !== id));
  }, []);

  return {
    notifications,
    fetchNotifications,
    markAsRead,
    applySocketMessage,
    applyTicketEvent,
    applyContactDelete,
  };
};

export default useNotificationsData;
